import { Component, Injectable, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";

export interface ConfirmDialogData {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  mode: "confirm" | "alert";
  okText?: string;
}

@Component({
  selector: "app-confirm-dialog",
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <div class="confirm-dialog">
      <h2 mat-dialog-title>
        {{ data.title || (data.mode === "alert" ? "Notice" : "Please confirm") }}
      </h2>
      <mat-dialog-content>
        <p class="confirm-message">{{ data.message }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        @if (data.mode === "confirm") {
          <button mat-button (click)="ref.close(false)">
            {{ data.cancelText || "Cancel" }}
          </button>
        }
        <button
          mat-flat-button
          [class.destructive]="data.destructive"
          (click)="ref.close(true)"
        >
          {{
            data.mode === "alert"
              ? data.okText || "OK"
              : data.confirmText || "Confirm"
          }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .confirm-dialog {
        color: var(--ink-900);
        width: 100%;
        max-width: min(420px, calc(100vw - 32px));
      }
      h2 {
        font-weight: 700;
        color: var(--ink-900);
      }
      .confirm-message {
        color: var(--ink-700);
        line-height: 1.5;
        margin: 0;
      }
      [mat-dialog-actions] {
        gap: 8px;
        padding-top: 8px;
      }
      button[mat-flat-button] {
        background: var(--ink-900);
        color: #fff;
        border-radius: 10px;
      }
      button[mat-flat-button].destructive {
        background: #dc2626;
      }
    `,
  ],
})
export class ConfirmDialogComponent {
  readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<ConfirmDialogComponent, boolean>);
}

@Injectable({ providedIn: "root" })
export class ConfirmDialogService {
  private readonly dialog = inject(MatDialog);

  confirm(opts: {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    destructive?: boolean;
  }): Promise<boolean> {
    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: { ...opts, mode: "confirm" as const },
          panelClass: "confirm-dialog-panel",
          autoFocus: false,
        })
        .afterClosed(),
    ).then((result) => result === true);
  }

  alert(opts: {
    title?: string;
    message: string;
    okText?: string;
  }): Promise<void> {
    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: { ...opts, mode: "alert" as const },
          panelClass: "confirm-dialog-panel",
          autoFocus: false,
        })
        .afterClosed(),
    ).then(() => undefined);
  }
}
